import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Dimension, VocabStatus, VocabValue } from './types';

interface Props {
  values: VocabValue[];
  products: string[];
  onUpdate: (
    valueId: string,
    patch: { label?: string; definition?: string; status?: VocabStatus },
  ) => Promise<void>;
  onAdd: (value: {
    dimension: Dimension;
    product: string | null;
    label: string;
    definition: string;
  }) => Promise<void>;
}

const STATUS_ORDER: VocabStatus[] = ['proposed', 'approved', 'retired'];
const STATUS_HEADINGS: Record<VocabStatus, string> = {
  proposed: 'Waiting for your review',
  approved: 'Approved (used for tagging)',
  retired: 'Retired',
};
const SOURCE_LABELS: Record<string, string> = {
  ai_draft: 'AI draft',
  ai_suggested: 'AI suggestion',
  human: 'Added by hand',
};

interface Editing {
  valueId: string | null;
  dimension: Dimension;
  product: string | null;
  label: string;
  definition: string;
}

function ValueRow({
  value,
  onUpdate,
  onEdit,
}: {
  value: VocabValue;
  onUpdate: Props['onUpdate'];
  onEdit: (value: VocabValue) => void;
}) {
  const [busy, setBusy] = useState(false);
  const act = async (status: VocabStatus) => {
    setBusy(true);
    try {
      await onUpdate(value.value_id, { status });
    } catch {
      // The tab's error banner already shows the API's message.
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderTop: '1px solid #E2E8F0',
        opacity: value.status === 'retired' ? 0.6 : 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {value.label}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={SOURCE_LABELS[value.source] || value.source}
          />
          {value.evidence_count != null && (
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              seen in ~{value.evidence_count} creative
              {value.evidence_count === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
        {value.definition && (
          <Typography
            variant="body2"
            sx={{ color: '#475569', fontSize: '0.8125rem', mt: 0.25 }}
          >
            {value.definition}
          </Typography>
        )}
      </Box>
      <Tooltip title="Edit name or definition">
        <IconButton size="small" onClick={() => onEdit(value)} disabled={busy}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {value.status === 'proposed' && (
        <>
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={() => act('approved')}
          >
            Approve
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => act('retired')}
          >
            Reject
          </Button>
        </>
      )}
      {value.status === 'approved' && (
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => act('retired')}
        >
          Retire
        </Button>
      )}
      {value.status === 'retired' && (
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => act('approved')}
        >
          Restore
        </Button>
      )}
    </Box>
  );
}

// The two controlled vocabularies: brand-wide personas, and per-product selling
// points. Tags are only ever drawn from APPROVED values; proposals from the AI
// wait here until someone approves or rejects them.
export default function VocabularyPanel({
  values,
  products,
  onUpdate,
  onAdd,
}: Props) {
  const [tab, setTab] = useState<string>('persona');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isPersona = tab === 'persona';
  const shown = useMemo(
    () =>
      values.filter(v =>
        isPersona
          ? v.dimension === 'persona'
          : v.dimension === 'selling_point' && v.product === tab,
      ),
    [values, tab, isPersona],
  );
  const proposedCount = (key: string) =>
    values.filter(
      v =>
        v.status === 'proposed' &&
        (key === 'persona' ? v.dimension === 'persona' : v.product === key),
    ).length;

  const openAdd = () => {
    setSaveError(null);
    setEditing({
      valueId: null,
      dimension: isPersona ? 'persona' : 'selling_point',
      product: isPersona ? null : tab,
      label: '',
      definition: '',
    });
  };
  const openEdit = (v: VocabValue) => {
    setSaveError(null);
    setEditing({
      valueId: v.value_id,
      dimension: v.dimension,
      product: v.product,
      label: v.label,
      definition: v.definition || '',
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (editing.valueId) {
        await onUpdate(editing.valueId, {
          label: editing.label,
          definition: editing.definition,
        });
      } else {
        await onAdd({
          dimension: editing.dimension,
          product: editing.product,
          label: editing.label,
          definition: editing.definition,
        });
      }
      setEditing(null);
    } catch (err: any) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const tabLabel = (key: string, label: string) => {
    const n = proposedCount(key);
    return n ? `${label} (${n} to review)` : label;
  };

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, mb: 2, overflow: 'hidden' }}
    >
      <Box sx={{ px: 2.5, pt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Tag lists
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B' }}>
          Every creative is tagged from these shared lists only, so the same
          persona or selling point shows up across many creatives and can be
          compared. Keep lists short and distinct.
        </Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1 }}>
          <Tab
            value="persona"
            label={tabLabel('persona', 'Personas (all products)')}
          />
          {products.map(p => (
            <Tab
              key={p}
              value={p}
              label={tabLabel(p, `Selling points: ${p}`)}
            />
          ))}
        </Tabs>
      </Box>

      {STATUS_ORDER.map(status => {
        const group = shown.filter(v => v.status === status);
        if (!group.length) return null;
        return (
          <Box key={status}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                px: 2,
                pt: 1.5,
                pb: 0.5,
                color: '#64748B',
                fontWeight: 600,
                letterSpacing: '0.08em',
              }}
            >
              {STATUS_HEADINGS[status]} · {group.length}
            </Typography>
            {group.map(v => (
              <ValueRow
                key={v.value_id}
                value={v}
                onUpdate={onUpdate}
                onEdit={openEdit}
              />
            ))}
          </Box>
        );
      })}
      {!shown.length && (
        <Typography variant="body2" sx={{ px: 2.5, py: 2, color: '#64748B' }}>
          Nothing here yet. Tag some creatives, then use "Draft / suggest list
          values", or add a value by hand.
        </Typography>
      )}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #E2E8F0' }}>
        <Button size="small" onClick={openAdd}>
          + Add {isPersona ? 'a persona' : `a ${tab} selling point`}
        </Button>
      </Box>

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing?.valueId ? 'Edit value' : 'Add value'}
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: '8px !important',
          }}
        >
          <TextField
            label="Name"
            value={editing?.label || ''}
            onChange={e =>
              editing && setEditing({ ...editing, label: e.target.value })
            }
            autoFocus
          />
          <TextField
            label="Definition (what counts, so two people would tag the same way)"
            value={editing?.definition || ''}
            onChange={e =>
              editing && setEditing({ ...editing, definition: e.target.value })
            }
            multiline
            minRows={2}
          />
          {!editing?.valueId && (
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Values you add yourself are approved straight away.
            </Typography>
          )}
          {saveError && (
            <Typography variant="body2" sx={{ color: '#DC2626' }}>
              {saveError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || !editing?.label.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
